import { selectUserProjectTimeZone } from 'core/userContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Breadcrumb } from 'ui/breadcrumb';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { body2, borderColors, colors, heading3, spacing, subtitle2 } from 'ui/theme';
import { CampaignAlert } from '../../hooks';
import {
  getAlertHistoryItemCaptionDisplayText,
  getAlertHistoryItemLabelDisplayText,
} from './AlertsHistorySlideOver.utils';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
  border-bottom: 1px solid ${borderColors.borderPrimarySubtle};
  padding: ${spacing.plusHalf};
`;

const Title = styled.h3`
  ${heading3}
  margin-bottom: ${spacing.threeEights};
  margin-top: ${spacing.half};
`;

const SectionTitle = styled.div`
  ${subtitle2}
`;

const Body = styled.div`
  ${body2}
  color: ${colors.textSecondary};
`;

export const AlertHistoryDetails = ({
  alert,
  onBack,
}: {
  alert: CampaignAlert;
  onBack: () => void;
}) => {
  const { thresholdText: alertTriggeredThresholdText, frequencyText: alertTriggeredFrequencyText } =
    getAlertHistoryItemLabelDisplayText(alert, false); // Triggered alert threshold and frequency text to display

  const {
    thresholdText: alertConfiguredThresholdText,
    frequencyText: alertConfiguredFrequencyText,
  } = getAlertHistoryItemLabelDisplayText(alert, true); // Configured alert threshold and frequency text to display

  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const triggeredTimeText = getAlertHistoryItemCaptionDisplayText(
    alert?.triggeredTime,
    projectTimeZone,
  );

  return (
    <div data-test="alert-details-wrapper">
      <Section data-test="alert-details-triggered-alert-section">
        <div>
          <Breadcrumb
            isButton={true}
            breadcrumb={{
              content: 'All alerts',
              onClick: onBack,
            }}
            variant={PAGE_HEADER_VARIANTS.SECONDARY}
          />
          <Title>{alertTriggeredThresholdText}</Title>
          <Body>{alertTriggeredFrequencyText}</Body>
        </div>
      </Section>

      <Section data-test="alert-details-configured-alert-section">
        <div>
          <SectionTitle>Alert</SectionTitle>
          <Body>
            {alertConfiguredThresholdText} {alertConfiguredFrequencyText}
          </Body>
        </div>
        <div>
          <SectionTitle>Triggered on</SectionTitle>
          <Body>{triggeredTimeText}</Body>
        </div>
      </Section>
    </div>
  );
};
